/**
 * vl-animations.js — Vitality Lab Co.
 * Vanilla JS animation system. Self-initialising. No external libraries.
 * Guard: window.__vlAnimationsLoaded prevents double-init.
 */
(function () {
  'use strict';
  if (window.__vlAnimationsLoaded) return;
  window.__vlAnimationsLoaded = true;

  /* ── Utility ─────────────────────────────────────────── */
  function lerp(a, b, t) { return a + (b - a) * t; }

  /* ── 1. Cursor Glow (ShapeBlur) ──────────────────────── */
  function initCursorGlow() {
    if (window.matchMedia('(hover: none)').matches) return;
    var glow = document.createElement('div');
    glow.id = 'vl-cursor-glow';
    Object.assign(glow.style, {
      position: 'fixed', top: '0', left: '0',
      width: '48px', height: '48px',
      borderRadius: '50%',
      background: 'var(--vl-gold-soft)',
      filter: 'blur(20px)',
      pointerEvents: 'none',
      zIndex: '9998',
      opacity: '0',
      transition: 'opacity 400ms',
      willChange: 'transform'
    });
    document.body.appendChild(glow);
    var mx = 0, my = 0, cx = 0, cy = 0;
    document.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY; glow.style.opacity = '1';
    });
    document.addEventListener('mouseleave', function () { glow.style.opacity = '0'; });
    (function tick() {
      cx = lerp(cx, mx, 0.10);
      cy = lerp(cy, my, 0.10);
      glow.style.transform = 'translate3d(' + (cx - 24) + 'px,' + (cy - 24) + 'px,0)';
      requestAnimationFrame(tick);
    })();
  }

  /* ── 2. Hero Tilt ────────────────────────────────────── */
  function initHeroTilt() {
    var layer = document.querySelector('.vl-hero__tilt-layer');
    if (!layer) return;
    var hero = layer.closest('.vl-hero');
    if (!hero) return;
    if (window.matchMedia('(hover: none)').matches) return;
    var tx = 0, ty = 0, cx = 0, cy = 0, MAX = 6;
    hero.addEventListener('mousemove', function (e) {
      var r = hero.getBoundingClientRect();
      tx = ((e.clientY - r.top)  / r.height - 0.5) * -MAX;
      ty = ((e.clientX - r.left) / r.width  - 0.5) *  MAX;
    });
    hero.addEventListener('mouseleave', function () { tx = 0; ty = 0; });
    (function tick() {
      cx = lerp(cx, tx, 0.07);
      cy = lerp(cy, ty, 0.07);
      layer.style.transform =
        'perspective(1200px) rotateX(' + cx + 'deg) rotateY(' + cy + 'deg)';
      requestAnimationFrame(tick);
    })();
  }

  /* ── 3. BlurText (replay-aware; CSS drives reveal via .is-in) ─ */
  function initBlurText() {
    var nodes = document.querySelectorAll('.vl-blur-text:not([data-vl-bt-init])');
    nodes.forEach(function (node) {
      node.setAttribute('data-vl-bt-init', '1');
      var idx = 0;
      var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      var textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach(function (textNode) {
        var fragment = document.createDocumentFragment();
        textNode.nodeValue.split(/(\s+)/).forEach(function (word) {
          if (!word || /^\s+$/.test(word)) {
            fragment.appendChild(document.createTextNode(word));
            return;
          }
          var span = document.createElement('span');
          span.className = 'vl-bt-word';
          span.style.setProperty('--vl-bt-i', idx);
          span.textContent = word;
          fragment.appendChild(span);
          idx += 1;
        });
        textNode.parentNode.replaceChild(fragment, textNode);
      });
      node.classList.add('is-in');
    });
  }

  /* ── 4. Magnetic Buttons ─────────────────────────────── */
  function initMagneticButtons() {
    if (window.matchMedia('(hover: none)').matches) return;
    var btns = document.querySelectorAll('.vl-btn--primary, .vl-btn--gold, .vl-magnetic');
    btns.forEach(function (btn) {
      if (btn.dataset.magInit) return;
      btn.dataset.magInit = '1';
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width  / 2)) * 0.28;
        var dy = (e.clientY - (r.top  + r.height / 2)) * 0.28;
        btn.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.transform = '';
      });
    });
  }

  /* ── 4b. Card Tilt (3D hover parallax) ──────────────── */
  function initCardTilt() {
    if (window.matchMedia('(hover: none)').matches) return;
    var cards = document.querySelectorAll('.vl-tilt-card:not([data-tilt-init])');
    cards.forEach(function (card) {
      card.setAttribute('data-tilt-init', '1');
      var MAX = 4;
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        var rx = ((e.clientY - r.top)  / r.height - 0.5) * -MAX * 2;
        var ry = ((e.clientX - r.left) / r.width  - 0.5) *  MAX * 2;
        card.style.transform =
          'perspective(900px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg) translateZ(6px)';
      });
      card.addEventListener('mouseleave', function () {
        card.style.transform = '';
      });
    });
  }

  /* ── 5. ShinyText ────────────────────────────────────── */
  function initShinyText() { /* purely CSS-driven via .is-in toggle; see vl-design-system.css */ }

  /* ── 6. SpotlightCard ────────────────────────────────── */
  function initSpotlightCards() {
    var cards = document.querySelectorAll('.vl-spotlight-card:not([data-sp-init])');
    if (!cards.length) return;
    if (window.matchMedia('(hover: none)').matches) return;
    cards.forEach(function (card) {
      card.setAttribute('data-sp-init', '1');
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        card.style.setProperty('--mouse-x', (e.clientX - r.left) + 'px');
        card.style.setProperty('--mouse-y', (e.clientY - r.top)  + 'px');
      });
      card.addEventListener('mouseenter', function () { card.classList.add('is-hovered'); });
      card.addEventListener('mouseleave', function () { card.classList.remove('is-hovered'); });
    });
  }

  /* ── 7. ScrollVelocity Ticker ──────────────────────────
     Speeds the ticker up briefly on fast scroll, then decays back to
     the user's configured base speed. Two bugs were fixed here:
       - Selector was `.vl-ticker__rail` (BEM style); the actual class
         is `.vl-ticker-rail` (hyphen), so the JS used to no-op silently.
       - BASE was hardcoded to 28, which would override the customizer
         slider whenever the JS fired. Now read from the rail's computed
         CSS var (which inherits from the section's inline style). */
  function initScrollVelocity() {
    var rails = document.querySelectorAll('.vl-ticker-rail');
    if (!rails.length) return;
    function readBaseFor(rail) {
      var raw = getComputedStyle(rail).getPropertyValue('--vl-ticker-speed').trim();
      var n = parseFloat(raw);
      return isFinite(n) && n > 0 ? n : 28;
    }
    rails.forEach(function (rail) {
      var BASE = readBaseFor(rail);
      var lastY = window.scrollY, timer;
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        var vel = Math.abs(y - lastY);
        lastY = y;
        var factor = Math.max(0.4, Math.min(3, 1 + vel / 200));
        rail.style.setProperty('--vl-ticker-speed', (BASE / factor).toFixed(2) + 's');
        clearTimeout(timer);
        timer = setTimeout(function () {
          rail.style.setProperty('--vl-ticker-speed', BASE + 's');
        }, 350);
      }, { passive: true });
    });
  }

  /* ── 8. DotGrid (canvas) ─────────────────────────────── */
  function initDotGrid() {
    var canvases = document.querySelectorAll('canvas.vl-dot-grid:not([data-dg-init])');
    if (!canvases.length) return;
    var SPACING = 28, DOT_R = 1.5, RADIUS = 120;
    var GOLD = 'rgba(209,167,119,';
    canvases.forEach(function (canvas) {
      canvas.setAttribute('data-dg-init', '1');
      var section = canvas.closest('section') || canvas.parentElement;
      var ctx = canvas.getContext('2d');
      var dots = [], mouse = { x: -9999, y: -9999 }, rafId;
      function buildGrid() {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        dots = [];
        for (var x = SPACING / 2; x < canvas.width; x += SPACING) {
          for (var y = SPACING / 2; y < canvas.height; y += SPACING) {
            dots.push({ x: x, y: y, a: 0.08 });
          }
        }
      }
      function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (var i = 0; i < dots.length; i++) {
          var d = dots[i];
          var dist = Math.sqrt(Math.pow(d.x - mouse.x, 2) + Math.pow(d.y - mouse.y, 2));
          var target = dist < RADIUS ? 0.08 + (1 - dist / RADIUS) * 0.24 : 0.08;
          d.a = lerp(d.a, target, 0.08);
          ctx.beginPath();
          ctx.arc(d.x, d.y, DOT_R, 0, Math.PI * 2);
          ctx.fillStyle = GOLD + d.a.toFixed(3) + ')';
          ctx.fill();
        }
        rafId = requestAnimationFrame(draw);
      }
      if (!window.matchMedia('(hover: none)').matches) {
        section.addEventListener('mousemove', function (e) {
          var r = canvas.getBoundingClientRect();
          mouse.x = e.clientX - r.left;
          mouse.y = e.clientY - r.top;
        });
        section.addEventListener('mouseleave', function () { mouse.x = -9999; mouse.y = -9999; });
      }
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (!dots.length) buildGrid();
            if (!rafId) draw();
          } else {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        });
      }, { threshold: 0 });
      obs.observe(canvas);
      window.addEventListener('resize', function () { if (dots.length) buildGrid(); });
    });
  }

  /* ── 9. Paint Reveal (hero canvas) ───────────────────── */
  function initPaintReveal() {
    var stages = document.querySelectorAll('.vl-paint-stage[data-paint-enabled="true"]:not([data-paint-init])');
    if (!stages.length) return;

    var hover = window.matchMedia('(hover: hover)').matches;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    stages.forEach(function (stage) {
      stage.setAttribute('data-paint-init', '1');

      if (!hover || reduced) {
        stage.classList.add('vl-kenburns');
        var canvas = stage.querySelector('.vl-paint-stage__canvas');
        if (canvas) canvas.remove();
        return;
      }

      var canvas = stage.querySelector('.vl-paint-stage__canvas');
      var topImg = stage.querySelector('.vl-paint-stage__layer--top');
      if (!canvas || !topImg) return;

      var brush     = parseFloat(stage.getAttribute('data-brush-size')) || 90;
      var softness  = parseFloat(stage.getAttribute('data-stroke-softness')) || 60;
      var delay     = parseFloat(stage.getAttribute('data-recover-delay')) || 1500;
      var speed     = parseFloat(stage.getAttribute('data-recover-speed')) || 700;

      var ctx, dpr, W, H, img, strokes = [], rafId = null;

      function resize() {
        var rect = stage.getBoundingClientRect();
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        W = rect.width; H = rect.height;
        canvas.width  = W * dpr;
        canvas.height = H * dpr;
        canvas.style.width  = W + 'px';
        canvas.style.height = H + 'px';
        ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        paintTop();
      }

      function paintTop() {
        if (!img || !img.complete) return;
        var iar = img.naturalWidth / img.naturalHeight;
        var car = W / H;
        var dw, dh, dx, dy;
        if (iar > car) { dh = H; dw = H * iar; dx = (W - dw) / 2; dy = 0; }
        else           { dw = W; dh = W / iar; dx = 0; dy = (H - dh) / 2; }
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(img, dx, dy, dw, dh);
      }

      function applyStrokes(now) {
        // Mechanism: each frame, paintTop() has just redrawn the full top
        // image onto the canvas. We then "punch holes" by erasing where each
        // active stroke lives. Stroke ages [0, delay) = full erase. Ages
        // [delay, delay+speed) = erase strength fades linearly to 0 (smooth
        // recover). Ages >= delay+speed = stroke removed; that region simply
        // doesn't get erased next frame, so it stays fully painted.
        ctx.globalCompositeOperation = 'destination-out';
        for (var i = strokes.length - 1; i >= 0; i--) {
          var s = strokes[i];
          var age = now - s.t;
          var totalLife = delay + speed;
          if (age >= totalLife) {
            strokes.splice(i, 1);
            continue;
          }
          var strength = age < delay ? 1 : 1 - (age - delay) / speed;
          ctx.globalAlpha = strength;
          ctx.beginPath();
          ctx.arc(s.x, s.y, brush / 2, 0, Math.PI * 2);
          ctx.shadowColor = 'rgba(0,0,0,1)';
          ctx.shadowBlur = softness;
          ctx.fillStyle = 'rgba(0,0,0,1)';
          ctx.fill();
        }
        // Reset state for any subsequent draws.
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }

      function loop() {
        if (!ctx) return;
        var now = performance.now();
        paintTop();
        applyStrokes(now);
        if (strokes.length) {
          rafId = requestAnimationFrame(loop);
        } else {
          rafId = null;
        }
      }

      function addStroke(e) {
        var rect = canvas.getBoundingClientRect();
        var x = e.clientX - rect.left;
        var y = e.clientY - rect.top;
        strokes.push({ x: x, y: y, t: performance.now() });
        if (rafId == null) rafId = requestAnimationFrame(loop);
      }

      img = new Image();
      // No crossOrigin: we only draw the image, never read pixels via
      // toDataURL/getImageData. Setting crossOrigin='anonymous' causes the
      // load to fail when Shopify CDN omits CORS headers on a given variant.
      img.onload = resize;
      img.src = topImg.currentSrc || topImg.src;
      // If the source image already finished loading before the JS attached
      // (common for eager-loaded hero images), onload won't fire — kick resize.
      if (topImg.complete && topImg.naturalWidth > 0) {
        img.src = topImg.currentSrc || topImg.src;
        if (img.complete) resize();
      }

      canvas.addEventListener('mousemove', addStroke, { passive: true });
      canvas.addEventListener('touchmove', function (e) {
        var t = e.touches[0]; if (t) addStroke({ clientX: t.clientX, clientY: t.clientY });
      }, { passive: true });
      window.addEventListener('resize', resize, { passive: true });
      if (document.readyState !== 'complete') {
        window.addEventListener('load', resize, { once: true });
      }
    });
  }

  /* ── Boot ────────────────────────────────────────────── */
  function boot() {
    initCursorGlow();
    initHeroTilt();
    initBlurText();
    initShinyText();
    initSpotlightCards();
    initScrollVelocity();
    initDotGrid();
    initMagneticButtons();
    initCardTilt();
    initPaintReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init on Shopify theme editor section reloads */
  document.addEventListener('shopify:section:load', function () {
    initBlurText();
    initShinyText();
    initSpotlightCards();
    initScrollVelocity();
    initDotGrid();
    initMagneticButtons();
    initCardTilt();
    initPaintReveal();
  });

})();
